import React, { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { usePaidListNINConfigMigration_migrateCurrentProjectMutation as MigrateCurrentProjectMutation } from '@atlassian/jira-relay/src/__generated__/usePaidListNINConfigMigration_migrateCurrentProjectMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { MutationErrorCollection } from '@atlassian/jira-mutation-error/src/mutation-error-collection.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import {
	getJqlFromMigrationData,
	getPaidJqlFromMigrationData,
	getPaidMigrationDataForProject,
	getConfiguredProjectIdsFromLocalStorage,
	type PaidMigrationData,
} from './utils.tsx';
import messages from './messages.tsx';
import { InProgressFlag } from './InProgressFlag.tsx';

const IN_PROGRESS_FLAG_ID = 'paid-list-ninconfig-migration-in-progress';
const IN_PROGRESS_FLAG_CONFIG = {
	id: IN_PROGRESS_FLAG_ID,
	type: 'info',
	messageType: 'transactional',
	messageId: 'issue-navigator.services.list-nin-config-migration.in-progress-flag-config.info',
	render: () => <InProgressFlag />,
};
const MAX_PROJECTS_PER_REQUEST = 100;

type UsePaidListNINConfigMigrationProps = {
	currentProjectId?: string | null;
	currentProjectKey?: string | null;
	onChangeJqlAndGroupBy?: (params: { jql?: string; groupBy?: string }) => void;
	onMigrationCompleted?: (fieldId: string | null, jql?: string) => void;
};

type PaidUserMigrationProjectData = {
	projectId: number;
	columns?: string[];
	jql?: string;
	groupBy?: string;
};

function chunkArray(
	projectData: PaidUserMigrationProjectData[],
	size: number,
): PaidUserMigrationProjectData[][] {
	const out = [];
	for (let i = 0; i < projectData.length; i += size) out.push(projectData.slice(i, i + size));
	return out;
}

export const usePaidListNINConfigMigration = ({
	currentProjectId,
	currentProjectKey,
	onChangeJqlAndGroupBy,
	onMigrationCompleted,
}: UsePaidListNINConfigMigrationProps): {
	migrateCurrentProject: () => void;
	migrateAllProjects: () => void;
} => {
	const cloudId = useCloudId();
	const { showFlag, dismissFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const handleError = useCallback(
		(error: Error, onRetry: () => void) => {
			dismissFlag(IN_PROGRESS_FLAG_ID);
			// TODO: https://jbusiness.atlassian.net/browse/JSC-3254
			fireErrorAnalytics({
				meta: {
					id: 'paidListNINConfigMigration',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
			showFlag({
				type: 'error',
				title: messages.listMigrationErrorFlag,
				messageType: 'transactional',
				messageId: 'issue-navigator.services.list-nin-config-migration.show-flag.error',
				actions: [
					{
						content: formatMessage(messages.listMigrationTryAgainAction),
						onClick: onRetry,
					},
				],
			});
		},
		[showFlag, formatMessage, dismissFlag],
	);

	const handleSuccess = useCallback(
		(migrationData: PaidMigrationData | null) => {
			dismissFlag(IN_PROGRESS_FLAG_ID);
			showFlag({
				type: 'success',
				title: messages.listMigrationSuccessFlag,
				messageType: 'transactional',
				messageId: 'issue-navigator.services.list-nin-config-migration.show-flag.success',
				isAutoDismiss: true,
			});
			if (!migrationData || !currentProjectKey) return;
			const jql = getJqlFromMigrationData(migrationData, currentProjectKey);

			onMigrationCompleted?.(migrationData?.groupBy ?? null, jql);

			// Updates JQL and groupBy for current project if current project has had settings merged
			onChangeJqlAndGroupBy?.({
				jql,
				groupBy: migrationData?.groupBy ?? undefined,
			});
		},
		[onChangeJqlAndGroupBy, showFlag, dismissFlag, currentProjectKey, onMigrationCompleted],
	);

	const [migrateCurrentProjectMutation] = useMutation<MigrateCurrentProjectMutation>(graphql`
		mutation usePaidListNINConfigMigration_migrateCurrentProjectMutation(
			$input: JiraListSettingMigrationInput!
		) {
			jira_listSettingMigrationMutation(input: $input) {
				success
				__typename
				errors {
					message
					extensions {
						statusCode
					}
				}
			}
		}
	`);

	const migrateCurrentProject = useCallback(() => {
		if (!currentProjectId) {
			return;
		}

		const paidMigrationData = getPaidMigrationDataForProject(currentProjectId);
		const jql = getPaidJqlFromMigrationData(paidMigrationData);
		const { groupBy, fieldsDetail } = paidMigrationData;
		showFlag(IN_PROGRESS_FLAG_CONFIG);
		migrateCurrentProjectMutation({
			variables: {
				input: {
					cloudId,
					nodes: [
						{
							projectId: Number(currentProjectId),
							columns: fieldsDetail?.order ?? undefined,
							jql: jql ?? undefined,
							groupBy: groupBy ?? undefined,
						},
					],
				},
			},
			onError: (error) => {
				handleError(error, migrateCurrentProject);
			},
			onCompleted: (response) => {
				if (response.jira_listSettingMigrationMutation?.success !== true) {
					handleError(
						new MutationErrorCollection(response.jira_listSettingMigrationMutation?.errors),
						migrateCurrentProject,
					);
					return;
				}
				handleSuccess(paidMigrationData);
			},
		});
	}, [
		currentProjectId,
		cloudId,
		migrateCurrentProjectMutation,
		handleError,
		handleSuccess,
		showFlag,
	]);

	const migrateAllProjects = useCallback(() => {
		const configuredProjectIds = getConfiguredProjectIdsFromLocalStorage();
		if (configuredProjectIds.length === 0) return;

		showFlag(IN_PROGRESS_FLAG_CONFIG);

		let currentProjectData: PaidMigrationData | null = null;

		const nodes: PaidUserMigrationProjectData[] = configuredProjectIds.map((projectId) => {
			const paidMigrationData = getPaidMigrationDataForProject(projectId);
			const jql = getPaidJqlFromMigrationData(paidMigrationData);
			const { groupBy, fieldsDetail } = paidMigrationData;

			if (projectId === currentProjectId) {
				currentProjectData = paidMigrationData;
			}

			return {
				projectId: Number(projectId),
				columns: fieldsDetail?.order ?? undefined,
				jql: jql ?? undefined,
				groupBy: groupBy ?? undefined,
			};
		});

		const chunks = chunkArray(nodes, MAX_PROJECTS_PER_REQUEST);

		const commitChunk = (chunkNodes: PaidUserMigrationProjectData[]) =>
			new Promise<void>((resolve, reject) => {
				migrateCurrentProjectMutation({
					variables: {
						input: {
							cloudId,
							nodes: chunkNodes,
						},
					},
					onCompleted: (response) => {
						if (response.jira_listSettingMigrationMutation?.success !== true) {
							reject(
								new MutationErrorCollection(response.jira_listSettingMigrationMutation?.errors),
							);
							return;
						}
						resolve();
					},
					onError: (error) => {
						reject(error);
					},
				});
			});

		Promise.allSettled(chunks.map(commitChunk)).then((results) => {
			const failed = results.find((r) => r.status === 'rejected');
			if (failed) {
				handleError(failed.reason, migrateAllProjects);
				return;
			}
			handleSuccess(currentProjectData);
		});
	}, [
		showFlag,
		currentProjectId,
		migrateCurrentProjectMutation,
		cloudId,
		handleSuccess,
		handleError,
	]);

	return { migrateCurrentProject, migrateAllProjects };
};
